// Google Analytics & Tag Manager utility functions for BirdSong app

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export const GA_MEASUREMENT_ID = 'G-PTKZDF87L7';

// Push data to GTM dataLayer
const pushToDataLayer = (data: any) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

// Initialize tracking
export const initGA = () => {
  if (typeof window !== 'undefined') {
    // GTM event
    pushToDataLayer({
      event: 'app_initialized',
      app_name: 'BirdSong',
      app_version: '1.0.0'
    });
    
    // Direct GA4 configuration
    if (window.gtag) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href,
      });
    }
  }
};

// Track page views
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  // GTM dataLayer
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: window.location.href
  });
  
  // Direct GA4 tracking
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });
  }
};

// Track custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  // GTM dataLayer event
  const eventData: any = {
    event: 'custom_event',
    event_action: action,
    event_category: category
  };
  
  if (label) {eventData.event_label = label;}
  if (value !== undefined) {eventData.event_value = value;}
  
  pushToDataLayer(eventData);
  
  // Direct GA4 event
  if (typeof window !== 'undefined' && window.gtag) {
    const gtagEventData: any = {
      event_category: category,
    };
    
    if (label) {gtagEventData.event_label = label;}
    if (value !== undefined) {gtagEventData.value = value;}
    
    window.gtag('event', action, gtagEventData);
  }
};

// BirdSong specific event tracking
export const trackBirdDetection = (birdName: string, confidence: number) => {
  trackEvent('bird_detected', 'detection', birdName, Math.round(confidence * 100));
};

export const trackRecordingStart = () => {
  trackEvent('recording_start', 'audio', 'detection_session');
};

export const trackRecordingStop = (duration: number, birdsDetected: number) => {
  trackEvent('recording_stop', 'audio', 'detection_session', duration);
  trackEvent('session_complete', 'detection', 'birds_found', birdsDetected);
};

export const trackNavigationClick = (destination: string) => {
  trackEvent('navigation_click', 'navigation', destination);
};

export const trackBirdCardClick = (birdName: string) => {
  trackEvent('bird_card_click', 'interaction', birdName);
};

export const trackAuthAction = (action: 'login' | 'signup' | 'logout') => {
  trackEvent(action, 'auth');
};

export const trackError = (error: string, component?: string) => {
  trackEvent('error', 'app_error', `${component ? component + ': ' : ''}${error}`);
};